import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import { BrowserCodeReader, BrowserQRCodeReader } from '@zxing/browser';
import { enqueueSnackbar } from 'notistack';
import { Fragment, useEffect, useRef, useState } from 'react';
import { _ } from '/@/locale';

export const checkVideoInput = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some((d) => d.kind === 'videoinput');
};

/** @param {{sx: import('@mui/material').SxProps, sxButton: import('@mui/material').SxProps, qrCodeSuccessCallback: VoidFunction}} props */
const QRCodeScanner = ({ sx, qrCodeSuccessCallback }) => {
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState(undefined);
    const [controls, setControls] = useState(undefined);
    const [codeReader, setCodeReader] = useState(undefined);
    const [switching, setSwitching] = useState(false);

    const videoRef = useRef(null);

    const switchDevice = () => {
        if (devices.length === 0) return;
        const deviceIndex = devices.findIndex((el) => el.deviceId === deviceId);

        let newDevideId;
        if (deviceIndex === devices.length - 1) {
            // Last device
            newDevideId = devices?.[0]?.deviceId;
        } else {
            newDevideId = devices[deviceIndex + 1].deviceId;
        }
        setDeviceId(newDevideId);
    };

    const close = () => {
        if (controls) {
            controls.stop();
        }
        if (codeReader) {
            codeReader.reader.reset();
        }

        setControls(undefined);
        setCodeReader(undefined);
        setDeviceId(undefined);
        setDevices([]);
    };

    const initScanQr = async () => {
        // Try access camera

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (stream.getVideoTracks().length === 0) {
                enqueueSnackbar(_('Debe permitir el acceso a la cámara para poder escanear el código.'), {
                    variant: 'error',
                });
                return;
            }

            const videoInputDevices = await BrowserCodeReader.listVideoInputDevices();
            setDevices(videoInputDevices);
            if (videoInputDevices.length === 0) return;
            else {
                const backCamera = videoInputDevices.find((el) => el.label.includes('back'));
                if (backCamera) {
                    setDeviceId(backCamera.deviceId);
                } else {
                    setDeviceId(videoInputDevices[videoInputDevices.length - 1].deviceId);
                }
            }
        } catch (error) {
            enqueueSnackbar(_('Debe permitir el acceso a la cámara para poder escanear el código.'), {
                variant: 'error',
            });
            return;
        }

        const CR = new BrowserQRCodeReader();
        CR.options.delayBetweenScanAttempts = 10;
        setCodeReader(CR);
    };

    useEffect(() => {
        setSwitching(true);
        if (!codeReader || !deviceId) return;
        codeReader.reader.reset();
        if (controls) {
            controls.stop();
        }

        (async () => {
            try {
                const newControls = await codeReader.decodeFromVideoDevice(
                    deviceId,
                    videoRef.current,
                    (result, error, controls) => {
                        if (result) {
                            close();
                            controls.stop();
                            qrCodeSuccessCallback(result.text);
                        }
                    }
                );
                setControls(newControls);
            } catch (error) {
                console.log(error);
            }
            setSwitching(false);
        })();
    }, [codeReader, deviceId]);

    return (
        <Box sx={sx}>
            <Button variant='contained' fullWidth onClick={() => initScanQr()} endIcon={<QrCodeIcon />}>
                {_('Escanear QR')}
            </Button>

            {codeReader && (
                <Fragment>
                    <Box
                        ref={videoRef}
                        component='video'
                        style={{
                            position: 'fixed',
                            right: 0,
                            top: '60px',
                            height: '100vh',
                            width: '100vw',
                            backgroundBlendMode: 'overlay',
                            background: '#808080cc',
                            zIndex: 1,
                        }}
                    ></Box>

                    {devices.length > 0 && (
                        <Box sx={{ position: 'fixed', right: '10px', top: '70px', zIndex: 2 }}>
                            {devices.length > 1 && (
                                <Button
                                    size='large'
                                    variant='contained'
                                    startIcon={<CameraswitchIcon />}
                                    onClick={switchDevice}
                                    sx={{ mx: 2 }}
                                    disabled={switching}
                                >
                                    {_('Cambiar camara')}
                                </Button>
                            )}
                            <Button
                                size='large'
                                variant='contained'
                                startIcon={<CloseIcon />}
                                onClick={() => close()}
                                color='warning'
                            >
                                {_('Cerrar')}
                            </Button>
                        </Box>
                    )}
                </Fragment>
            )}
        </Box>
    );
};

export default QRCodeScanner;
